<template>
    <div class="section-content pt-4" :class="{ 'mt-10': $vuetify.breakpoint.xs }">
        <div ref="rmTopSection">
            <hb-header full>
                <hb-page-header title="Recurring Task Manager"
                    description="Create recurring tasks for repeated workflows like daily cleanups and weekly maintenance.">
                </hb-page-header>
            </hb-header>
        </div>
        <div class="main-content-rtm mt-4">
            <hb-data-table-header rightColumns="12" v-if="items.length">
                <template v-slot:actions>
                    <div class="d-flex align-center justify-end">
                        <hb-text-field class="py-0 mr-2" width="307px" v-model="searchModel" search full placeholder="Search By"></hb-text-field>
                        <hb-btn small color="secondary" v-if="isAllowedtoCreateEdit" @click="showTodo = true">+ New recurring task</hb-btn>
                        <hb-btn icon class="ml-1" tooltip="Download" :active-state-off="true" @click="downloadReport" hover-color="#637381">mdi-download</hb-btn>
                    </div>
                </template>
            </hb-data-table-header>

            <hb-data-table :headers="headers" v-if="items.length" :loading="isFetchingTasks" :search="searchModel" class="frozen-recurring-columns" :items="items">
                <template v-slot:item.title="{ item }">
                    <hb-tooltip>
                        <template v-slot:item>
                            {{ item.title }}
                        </template>
                        <template v-slot:body>
                            {{ item.title }}
                        </template>
                    </hb-tooltip>
                </template>
                <template v-slot:item.details="{ item }">
                    <hb-tooltip>
                        <template v-slot:item>
                            {{ item.details }}
                        </template>
                        <template v-slot:body>
                            {{ item.details }}
                        </template>
                    </hb-tooltip>
                </template>
                <template v-slot:item.created_at="{ item }">
                    <hb-tooltip>
                        <template v-slot:item>
                            {{ item.created_at | formatLocalDateTime }}
                        </template>
                        <template v-slot:body>
                            {{ item.created_at | formatLocalDateTime }}
                        </template>
                    </hb-tooltip>
                </template>
                <template v-slot:item.updated_at="{ item }">
                    <hb-tooltip>
                        <template v-slot:item>
                            {{ item.updated_at | formatLocalDateTime }}
                        </template>
                        <template v-slot:body>
                            {{ item.updated_at | formatLocalDateTime }}
                        </template>
                    </hb-tooltip>
                </template>
                <template v-slot:item.actions="{ item }">
                    <hb-menu options class="d-flex justify-end">
                        <v-list>
                            <v-list-item v-if="isAllowedtoCreateEdit" @click="updateTaskEvent(item)">
                                <v-list-item-title>Edit</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="duplicateTask(item)">
                                <v-list-item-title>Duplicate</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="updateStatusEvent(item)">
                                <v-list-item-title>{{ item.status ? 'Deactivate' : 'Activate' }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="itemToDeleteEvent(item)">
                                <v-list-item-title>Delete</v-list-item-title>
                            </v-list-item>

                        </v-list>
                    </hb-menu>
                </template>
                <template v-slot:item.status="{ item }">
                    <span v-if="item.status">
                        <hb-icon color="success" small mdi-code="mdi-check-circle" />
                        <span class="ml-1">Active</span>
                    </span>
                    <span v-else>
                        <hb-icon color="error" small mdi-code="mdi-close-circle" />
                        <span class="ml-1">In-Active</span>
                    </span>
                </template>
            </hb-data-table>

            <hb-empty-state v-if="!isFetchingTasks && !items.length">
                <template v-slot:default>
                    <div class="empty-box-rtm">
                        <hb-icon>mdi-cached</hb-icon>
                        <div class="hb-empty-state-text-light mt-3 mb-2">No recurring task yet.</div>
                        <hb-btn color="secondary" v-if="isAllowedtoCreateEdit" @click="showTodo=true">Create new task</hb-btn>
                    </div>
                </template>
            </hb-empty-state>
        </div>
        <hb-modal
            size="medium"
            :title="deleteSelected ? 'Delete ' + deleteSelected.title : ''"
            v-model="showDeleteDialog"
            confirmation
            @close="clearDeleteData"
        >
            <template v-slot:content>
                <div class="py-4 px-6">
                    Are you sure you want to delete the selected task?
                    <br /><br />
                    This action cannot be undone.
                </div>
            </template>
            <template v-slot:left-actions>
            </template>
            <template v-slot:right-actions>
                <hb-btn color="destructive" :loading="isDeleting" :disabled="isDeleting" @click="deleteTaskEvent">Delete</hb-btn>
            </template>
        </hb-modal>
        <create-todo v-if="showTodo" :isCreateTodo="showTodo" :taskDetails="taskDetails" :isRecurring="true" @closeCreateTodo="closeTodo" />
    </div>
</template>

<script type="text/babel">

import api from '../../../assets/api';
import { notificationMixin } from '../../../mixins/notificationMixin.js';
import { mapGetters, mapActions } from 'vuex';
import * as XLSX from 'xlsx';

export default {
    name: "RecurringTasks",
    mixins: [notificationMixin],
    components: {
        'create-todo': () => import('./CreateTodo.vue')
    },
    data() {
        return {
            searchModel: '',
            showTodo: false,
            headers: [
                { text: "Title", value: "title", width: "100px" },
                { text: "Details", value: "details", sortable: false, width: "100px" },
                { text: "Type", value: "type", sortable: false, width: "100px" },
                { text: "Subtype", value: "subtype", sortable: false, width: "100px" },
                { text: "Associated With", value: "associated_with", sortable: false, width: "100px" },
                { text: "Created by", value: "created_by", sortable: false, width: "100px" },
                { text: "Created Date", value: "created_at", sortable: false, width: "100px" },
                { text: "Updated by", value: "updated_by", sortable: false, width: "100px" },
                { text: "Updated Date", value: "updated_at", sortable: false, width: "100px" },
                { text: "Status", value: "status", sortable: false, width: "100px" },
                { text: "", value: "actions", width: "56px" }
            ],
            items: [],
            deleteSelected: null,
            showDeleteDialog: false,
            isDeleting: false,
            isFetchingTasks: false,
            taskDetails: null
        }
    },
    created() {
        this.getTasks();
    },
    computed: {
        ...mapGetters({
            primaryRoleProperties: 'propertiesStore/filtered',
            hasPermission: 'authenticationStore/rolePermission'
        }),
        isAllowedtoCreateEdit() {
            return this.hasPermission('create_edit_recurring_task');
        }
        
    },
    methods: {
        async getTasks() {
            try {
                this.isFetchingTasks = true;
                let response = await api.get(this, api.RTM_CENTER);
                this.items = response.rtm_tasks.map(item => {
                    const association = Object.keys(item.Associations).find(key => item.Associations[key].length > 0);
                    const result = association ? association.charAt(0).toUpperCase() + association.slice(1) : 'Nothing';
                    return {
                        id: item.id,
                        name: item.config_name,
                        title: item.task_title,
                        details: item.task_details,
                        type: item.TaskType.label,
                        subtype: item.TaskType.name,
                        associated_with: result,
                        created_by: item.RtmTaskCadance[0].created_by_details ? item.RtmTaskCadance[0].created_by_details.first + ' ' + item.RtmTaskCadance[0].created_by_details.last : '',
                        created_at: item.RtmTaskCadance[0].created_at,
                        updated_by: item.RtmTaskCadance[0].modified_by_details ? item.RtmTaskCadance[0].modified_by_details.first + ' ' + item.RtmTaskCadance[0].modified_by_details.last : '',
                        updated_at: item.RtmTaskCadance[0].modified_at,
                        status: item.status ? true : false
                    }
                })
            } catch (error) {
                this.showMessageNotification({type: 'error', description: error});
            } finally {
                this.isFetchingTasks = false;
            }
        },
        async updateTaskEvent(params) {
            try {
                let response = await api.get(this, api.RTM_CENTER + params.id);
                let { rtm_item: task } = response;
                let mediaFiles = [];
                let documents = [];
                let assigness = [...task.Assignees.Contacts.map(item => {
                    return {
                        id: item.id,
                        name: item.first + ' ' + item.last,
                        is_admin: true
                    }
                }), ...task.Assignees.Roles.map(item => {
                    return {
                        id: item.id,
                        name: item.name,
                        is_role: true
                    }
                })];
                let property = [];
                let association = '';
                if (task.Associations?.lease?.length) association = 'contact';
                else if(task.Associations?.space?.length) association = 'space';
                else if(task.Associations?.property?.length) association = 'property';
                if (association === 'property') {
                    property = task.Associations?.property[0];
                }
                if (association === 'space' && this.primaryRoleProperties.find(itemInner => itemInner.id === task.Associations?.space[0].property_id)) {
                    let foundProperty = this.primaryRoleProperties.find(itemInner => itemInner.id === task.Associations?.space[0].property_id);
                    property = foundProperty;
                }
                if (association === 'contact' && this.primaryRoleProperties.find(itemInner => itemInner.id === task.Associations?.lease[0].Property.id)) {
                    let foundProperty = this.primaryRoleProperties.find(itemInner => itemInner.id === task.Associations?.lease[0].Property.id);
                    property = foundProperty;
                }
                if (task.Associations?.space?.length) {
                    property = this.primaryRoleProperties.find(itemInner => itemInner.id === task.Associations?.space[0].property_id);
                }

                if (task.Attachments?.length) { 
                    for (const element of task.Attachments) {
                        if (element.mimetype.includes('image') || element.mimetype.includes('video')) {
                            let videoThumbnail = '';
                            let isVideo = element.mimetype.includes('video');
                            if (isVideo) {
                                videoThumbnail = await this.createThumbnailFromBase64(element.data_url)
                            }
                            mediaFiles.push({
                                "id": element.id,
                                video: isVideo ? true : false,
                                video_src: `data:${element.mimetype};base64,${element.data_url}`,
                                src: isVideo && videoThumbnail ? videoThumbnail : `data:${element.mimetype};base64,${element.data_url}`,
                                thumbnail: videoThumbnail,
                                upload_id: element.upload_id
                            })
                        } else {
                            documents.push(element)
                        }
                    }
                }
                let RtmTaskCadance = task.RtmTaskCadance[0];
                this.taskDetails = {
                    id: task.id,
                    type: task.TaskType && task.TaskType.label.toLowerCase(),
                    subType: task.TaskType && task.TaskType.id,
                    title: task.task_title,
                    assignTo: assigness,
                    association: task.Associations?.contact?.length || task.Associations?.lease?.length ? 3 : task.Associations?.space?.length ? 2 : task.Associations?.property?.length ? 1 : 0,
                    selectedProperties: property ? [property] : [],
                    details: task.task_details,
                    hotTask: task.hot_task ? true : false,
                    dueDate: task.due_date ? task.due_date.split(' ')[0] : '',
                    startDate: task.start_date,
                    mediaFiles,
                    attachments: documents,
                    location: task.task_location === 'inside' ? 0 : 1,
                    associations: task.Associations ? task.Associations : [],
                    status: task.status,
                    createdBy: RtmTaskCadance.created_by_details,
                    config_name: task.config_name,
                    cadence: {
                        every: RtmTaskCadance.number,
                        type: RtmTaskCadance.cadence_type.charAt(0).toUpperCase() + RtmTaskCadance.cadence_type.slice(1),
                        weekDay: RtmTaskCadance.cadence_type === 'week' && RtmTaskCadance.priority_day ? RtmTaskCadance.priority_day.charAt(0).toUpperCase() + RtmTaskCadance.priority_day.slice(1) : '',
                        monthValue: {
                            type: RtmTaskCadance.priority_type ? 'week' : 'day',
                            day: RtmTaskCadance.cadence_type === 'month' && RtmTaskCadance.recurring_day ? ('Day ' + RtmTaskCadance.recurring_day) : '',
                            day2: RtmTaskCadance.priority_type && RtmTaskCadance.priority_day ? RtmTaskCadance.priority_day : '',
                            day2: RtmTaskCadance.priority_type && RtmTaskCadance.priority_day ? RtmTaskCadance.priority_day.charAt(0).toUpperCase() + RtmTaskCadance.priority_day.slice(1) : '',
                            repeat: RtmTaskCadance.priority_type ? RtmTaskCadance.priority_type.charAt(0).toUpperCase() + RtmTaskCadance.priority_type.slice(1) : ''
                        }
                    }
                };
                this.showTodo = true;
            } catch (error) {
                this.showMessageNotification({type: 'error', description: error});
            } finally {
            }
        },
        createThumbnailFromBase64(base64Video) {
            return new Promise((resolve, reject) => {
                const video = document.createElement("video");
                video.src = URL.createObjectURL(new Blob([Uint8Array.from(atob(base64Video), c => c.charCodeAt(0))], { type: "video/mp4" }));
                video.muted = true;
                video.autoplay = true;
                video.playsInline = true;

                video.onloadeddata = () => {
                    const canvas = document.createElement("canvas");
                    canvas.width = 300;
                    canvas.height = (video.videoHeight / video.videoWidth) * 300;
                    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
                    resolve(canvas.toDataURL("image/png"));
                };

                video.onerror = () => reject("Error loading video");
            });
        },
        itemToDeleteEvent(params) {
            this.deleteSelected = params;
            this.showDeleteDialog = true;
        },
        clearDeleteData() {
            this.deleteSelected = null;
            this.showDeleteDialog = false;
        },
        async deleteTaskEvent() {
            try {
                this.isDeleting = true;
                await api.put(this, api.RTM_CENTER + this.deleteSelected.id + '/delete');
                this.showMessageNotification({type: 'success', description: 'Deleted Successfully.'});
                this.clearDeleteData();
                this.getTasks();
            } catch (error) {
                this.showMessageNotification({type: 'error', description: error});
            } finally {
                this.isDeleting = false;
            }
        },
        async duplicateTask(params) {
            try {
                this.isFetchingTasks = true;
                await api.post(this, api.RTM_CENTER + params.id + '/duplicate');
                this.showMessageNotification({type: 'success', description: 'Duplicated Successfully.'});
                await this.getTasks();
            } catch (error) {
                this.showMessageNotification({type: 'error', description: error});
            } finally {
                this.isFetchingTasks = false;
            }
        },
        async updateStatusEvent(params) {
            try {
                this.isFetchingTasks = true;
                await api.put(this, api.RTM_CENTER + params.id + '/update-status', { status: params.status ? 0 : 1 });
                this.showMessageNotification({type: 'success', description: 'Status has been updated.'});
                await this.getTasks();
            } catch (error) {
                this.showMessageNotification({type: 'error', description: error});
            } finally {
                this.isFetchingTasks = false;
            }
        },
        closeTodo() {
            this.taskDetails = null;
            this.showTodo = false;
            this.getTasks();
        },
        downloadReport() {
            let sheetData = this.items.map(item => {
                return {
                    "Name": item.name,
                    "Title": item.title,
                    "Details": item.details,
                    "Type": item.type,
                    "Subtype": item.subtype,
                    "Associated With": item.associated_with,
                    "Created By": item.created_by,
                    "Created At": item.created_at,
                    "Updated By": item.updated_by,
                    "Updated At": item.updated_at,
                    "Status": item.status ? "Active" : "In-Active"
                }
            })
            const worksheet = XLSX.utils.json_to_sheet(sheetData, { skipHeader: false });
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
            const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array', cellStyles: true });

            const blob = new Blob([excelBuffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = URL.createObjectURL(blob);

            // Create and trigger anchor tag
            const a = document.createElement('a');
            a.href = url;
            a.download = 'data.xlsx';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            // Revoke URL
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        }

    },
}
</script>

<style scoped>
.empty-box-rtm {
    height: 257px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
}
</style>

<style lang="scss">
.frozen-recurring-columns {
  table {
    > thead, > tbody {
      > tr {

        > td, > th {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 100px;
        }

        // Styling for the first, second, and last columns
        th:first-child,
        th:nth-child(2),
        th:last-child,
        td:first-child,
        td:nth-child(2),
        td:last-child {
          position: sticky !important;
          position: -webkit-sticky !important;
          z-index: 3 !important;

          &:first-child {
            left: 0 !important;
            max-width: 100px;
          }

          &:nth-child(2) {
            left: 100px !important;

            &::after {
              content: "";
              position: absolute;
              top: 0;
              right: 0;
              bottom: 0;
              width: 2px;
              background: rgba(0, 0, 0, 0.1);
              box-shadow: 2px 0 5px rgba(0, 0, 0, 0.2);
              pointer-events: none;
            }
          }

          &:last-child {
            right: 0 !important;
            width: 45px;
            max-width: 45px;

            &::before {
              content: "";
              position: absolute;
              top: 0;
              left: 0;
              bottom: 0;
              width: 2px;
              background: rgba(0, 0, 0, 0.1);
              box-shadow: -2px 0 5px rgba(0, 0, 0, 0.2);
              pointer-events: none;
            }
          }
        }


        th:first-child,
        th:nth-child(2),
        th:last-child {
            background-color: #F4F6F8;
        }

        td:first-child,
        td:nth-child(2),
        td:last-child {
            background: white;
        }

        // Adjusting z-index for header cells
        th:first-child,
        th:nth-child(2),
        th:last-child {
          z-index: 4 !important;
        }
      }
    }
  }
}
</style>